import { apiFetch } from '../lib/api.js';
import { ApiError } from '../lib/errors.js';
import { getCanvasSyncClientId } from '../hooks/useCanvasSync.js';

const API_BASE = '/api/canvases';

async function fetchApi(url: string, options: RequestInit = {}) {
  const clientId = getCanvasSyncClientId();
  const response = await apiFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(clientId ? { 'X-Canvas-Client-Id': clientId } : {}),
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const errObj = body?.error;
    const message =
      (typeof errObj === 'object' && errObj?.message) ||
      (typeof errObj === 'string' && errObj) ||
      response.statusText;
    const code =
      (typeof errObj === 'object' && errObj?.code) || `HTTP_${response.status}`;
    throw new ApiError(message, code, response.status);
  }
  return response.json();
}

export const canvasService = {
  list: () => fetchApi(API_BASE),
  get: (id: string) => fetchApi(`${API_BASE}/${id}`),
  create: (data: { name?: string; description?: string }) => fetchApi(API_BASE, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => fetchApi(`${API_BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi(`${API_BASE}/${id}`, { method: 'DELETE' }),
  duplicate: (id: string) => fetchApi(`${API_BASE}/${id}/duplicate`, { method: 'POST' }),

  // Nodes
  addNode: (canvasId: string, node: any) => fetchApi(`${API_BASE}/${canvasId}/nodes`, { method: 'POST', body: JSON.stringify(node) }),
  updateNode: (canvasId: string, nodeId: string, data: any) => fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  bulkUpdateNodes: (canvasId: string, nodes: any[]) => fetchApi(`${API_BASE}/${canvasId}/nodes-bulk`, { method: 'PATCH', body: JSON.stringify({ nodes }) }),
  deleteNode: (canvasId: string, nodeId: string) => fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}`, { method: 'DELETE' }),

  // Edges
  addEdge: (canvasId: string, edge: any) => fetchApi(`${API_BASE}/${canvasId}/edges`, { method: 'POST', body: JSON.stringify(edge) }),
  deleteEdge: (canvasId: string, edgeId: string) => fetchApi(`${API_BASE}/${canvasId}/edges/${edgeId}`, { method: 'DELETE' }),

  // AI-powered prompt operations
  enhancePrompt: (canvasId: string, nodeId: string) =>
    fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}/enhance`, { method: 'POST' }),
  brainstormPrompt: (canvasId: string, nodeId: string) =>
    fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}/brainstorm`, { method: 'POST' }),

  // Upload
  uploadFile: async (canvasId: string, nodeId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const clientId = getCanvasSyncClientId();
    const response = await apiFetch(`${API_BASE}/${canvasId}/nodes/${nodeId}/upload`, {
      method: 'POST',
      body: formData,
      headers: clientId ? { 'X-Canvas-Client-Id': clientId } : undefined,
      // Don't set Content-Type — browser sets it with boundary for multipart
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const errObj = body?.error;
      const message =
        (typeof errObj === 'object' && errObj?.message) ||
        (typeof errObj === 'string' && errObj) ||
        response.statusText;
      const code =
        (typeof errObj === 'object' && errObj?.code) || `HTTP_${response.status}`;
      throw new ApiError(message, code, response.status);
    }
    return response.json();
  },

  // Generation
  generate: (canvasId: string, nodeId: string) => fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}/generate`, { method: 'POST' }),
  generateVideo: (canvasId: string, nodeId: string) => fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}/generate-video`, { method: 'POST' }),
  generateText: (canvasId: string, nodeId: string) => fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}/generate-text`, { method: 'POST' }),
  generateVariation: (canvasId: string, nodeId: string) => fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}/generate-variation`, { method: 'POST' }),
  generateTemplate: (canvasId: string, nodeId: string) => fetchApi(`${API_BASE}/${canvasId}/nodes/${nodeId}/generate-template`, { method: 'POST' }),

  // Models
  getTextModels: () => fetchApi(`${API_BASE}/text-models`),

  // Sharing
  getShareSettings: (canvasId: string) => fetchApi(`${API_BASE}/${canvasId}/share`),
  updateShare: (canvasId: string, data: { shareMode: 'public' | 'password' | null; password?: string }) =>
    fetchApi(`${API_BASE}/${canvasId}/share`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Shared canvas access (no org scope needed beyond auth)
  getSharedMeta: (token: string) => fetchApi(`/api/shared/${token}`),
  accessShared: (token: string, password?: string) =>
    fetchApi(`/api/shared/${token}/access`, { method: 'POST', body: JSON.stringify({ password }) }),
  duplicateShared: (token: string, password?: string) =>
    fetchApi(`/api/shared/${token}/duplicate`, { method: 'POST', body: JSON.stringify({ password }) }),
};
